import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';

import { CSSObject, Grid2 as Grid, styled, Theme } from '@mui/material';

import { ResponsiveGrid } from '../../../layout/grid';
import swiperClasses from '../../../styles/swiper-pagination.module.scss';
import {
  MarkdownContentWithImage,
  MarkdownContentWithImageDescriptionProps,
} from '../../markdown/markdown-content-with-image-description';
import VideoWithControls from '../../media/video-with-controls';
import { PaginationContainer } from '../../multimedia-gallery/swiper-elements';
import { QuickNavigation } from '../../quick-navigation/index';

export type StageSliderSlideData = {
  imageAltText?: string | null;
  image: React.ReactElement;
  video?: {
    desktopSources: { src: string; type: string }[];
    mobileSources?: { src: string; type: string }[];
    posterImage: React.ReactElement;
  } | null;
  textContent: string;
  showQuickNavigation?: boolean;
};

type Props = {
  slides: StageSliderSlideData[];
  quickNavigation?: React.ReactElement<typeof QuickNavigation>;
  pauseAnimation?: boolean;
  getAssetUrl: MarkdownContentWithImageDescriptionProps['getAssetUrl'];
};

export const SliderStage = React.memo(
  ({ slides, quickNavigation, pauseAnimation, getAssetUrl }: Props) => {
    const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null);
    const paginationRef = useRef<HTMLDivElement>(null);
    const [activeSlideIndex, changeActiveSlide] = useState(0);

    const changeSlide = (index: number) => {
      changeActiveSlide(index);
    };

    useEffect(() => {
      if (pauseAnimation) {
        swiperRef?.autoplay.pause();
      } else {
        swiperRef?.autoplay.resume();
      }
    }, [swiperRef?.autoplay, pauseAnimation]);

    const memoizedSliderImages = useMemo(() => {
      return slides.map((slide, idx) => (
        <Slide>
          {slide.video ? (
            <VideoContainer isActive={activeSlideIndex === idx}>
              <InnerVideoContainer>
                <StyledVideoWithControls
                  desktopSources={slide.video.desktopSources}
                  mobileSources={slide.video.mobileSources}
                  controls={false}
                  posterImage={slide.video.posterImage ?? slide.image}
                  autoplay
                  loop
                  muted
                  showAlwaysPauseButton
                />
              </InnerVideoContainer>
            </VideoContainer>
          ) : (
            <ImageContainer>{slide.image}</ImageContainer>
          )}
        </Slide>
      ));
    }, [slides, activeSlideIndex]);

    return (
      <SwiperContainer>
        <Swiper
          style={{ height: '100%', width: '100%' }}
          modules={[Autoplay, Pagination]}
          onSwiper={setSwiperRef}
          autoplay={{
            delay: 5_000,
          }}
          speed={750}
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          autoHeight={true}
          loop
          normalizeSlideIndex
          allowTouchMove={true}
          className={swiperClasses.swiper}
          onSlideChange={(swiper) => {
            changeSlide(swiper.realIndex);
          }}
        >
          {slides.map((slide, idx) => (
            <StyledSwiperSlide key={idx}>
              {memoizedSliderImages[idx]}

              <GradientContainer />
              <ContentWrapper>
                <GridWrapper>
                  <GlassContainer
                    size={{ xs: 12, sm: 7, md: 5 }}
                    onMouseEnter={() => swiperRef?.autoplay.stop()}
                    onMouseLeave={() => swiperRef?.autoplay.start()}
                  >
                    <InnerContainer>
                      <StyledMarkdownContainer
                        html={slide.textContent}
                        getAssetUrl={getAssetUrl}
                      />
                      {slide.showQuickNavigation && quickNavigation
                        ? quickNavigation
                        : null}
                    </InnerContainer>
                  </GlassContainer>
                </GridWrapper>
              </ContentWrapper>
            </StyledSwiperSlide>
          ))}
          <PaginationContainer ref={paginationRef} />
        </Swiper>
      </SwiperContainer>
    );
  },
);

const getAspectRatio = (theme: Theme) => ({
  [theme.breakpoints.up('sm')]: {
    aspectRatio: '900 / 560',
  },

  [theme.breakpoints.up('md')]: {
    aspectRatio: '1440 / 645',
  },

  [theme.breakpoints.up('lg')]: {
    aspectRatio: '1920 / 860',
  },
});

const SwiperContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  zIndex: 1,

  '.swiper': {
    marginBottom: '-800px',
    paddingBottom: '800px !important',
    overflowY: 'visible',
    pointerEvents: 'none',
  },
}));

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  position: 'relative',
  pointerEvents: 'auto',
  ...getAspectRatio(theme),
}));

const StyledVideoWithControls = styled(VideoWithControls)(({ theme }) => ({
  width: '100%',
  height: '100%',
  ...getAspectRatio(theme),
}));

const ImageContainer = styled('div')(({ theme }) => ({
  lineHeight: 0,

  'img, .gatsby-image-wrapper': {
    aspectRatio: '375 / 300',
    ...getAspectRatio(theme),
  },
}));

const imageVideoStyles = (isActive: boolean): CSSObject => ({
  width: '100%',
  transition: 'opacity var(--transition-duration)',
  zIndex: isActive ? 1 : 0,
  opacity: isActive ? 1 : 0,
});

const VideoContainer = styled('div')<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    ...imageVideoStyles(isActive),

    '.pause-button, .play-button': {
      position: 'absolute',
      top: 'var(--spacer-l)',
      right: 'var(--spacer-l)',
      width: '40px',
      height: '40px',

      [theme.breakpoints.up('sm')]: {
        bottom: 'var(--spacer-l)',
        top: 'auto',
        right: 'var(--spacer-l)',
        width: '70px',
        height: '70px',
      },
    },
  }),
);

const InnerVideoContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  ...getAspectRatio(theme),
}));

const Slide = styled('div')(({ theme }) => ({
  height: '100%',

  'img, .gatsby-image-wrapper': {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
  },
}));

const GradientContainer = styled('div')(({ theme }) => ({
  display: 'none',
  background:
    'linear-gradient(90deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 1) 26%, rgba(255, 255, 255, 0) 100%)',
  inset: '0 0 0 0',
  position: 'absolute',
  opacity: 0.75,
  width: '43%',

  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));

const ContentWrapper = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    zIndex: 1,
    left: 0,
    top: 0,
    height: '100%',
    width: '100%',
    pointerEvents: 'none',
  },
}));

const GridWrapper = styled(ResponsiveGrid)(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    marginTop: '-70px',
  },
}));

const GlassContainer = styled(Grid)(({ theme }) => ({
  boxSizing: 'content-box',
  padding: '20px',
  background: 'rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(17.5px)',
  borderRadius: 'var(--rounded-corners)',
  pointerEvents: 'all',

  '@supports not (backdrop-filter: blur(0))': {
    background: 'rgba(255, 255, 255, 0.7)',
  },

  [theme.breakpoints.up('sm')]: {
    marginLeft: '-30px',
    padding: '30px 0 30px 30px',
    marginTop: '40px',
  },

  [theme.breakpoints.up('md')]: {
    marginLeft: '-50px',
    padding: '30px 0 50px 50px',
  },

  [theme.breakpoints.up('lg')]: {
    marginTop: '75px',
    padding: '30px 0 50px 50px',
  },
}));

const InnerContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    paddingRight: '30px', // this is the right padding of the glass container to align text and container on the correct grid.
  },

  [theme.breakpoints.up('md')]: {
    paddingRight: '50px',
  },

  '.mewa-quick-nav': {
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      width: '90%',
    },

    [theme.breakpoints.up('md')]: {
      width: '88%',
    },
  },
}));

const StyledMarkdownContainer = styled(MarkdownContentWithImage)(
  ({ theme }) => ({
    marginBottom: '0 !important',

    h1: {
      ...theme.typography.h1,
      fontWeight: theme.typography.fontWeightLight,
      margin: '0 0 var(--spacer-m) 0',
    },

    h2: {
      ...theme.typography.h2,
      fontWeight: theme.typography.fontWeightLight,
      margin: 0,
    },

    h3: {
      ...theme.typography.h3,
      fontWeight: theme.typography.fontWeightLight,
      margin: '0',
    },

    h4: {
      ...theme.typography.h4,
      fontWeight: theme.typography.fontWeightRegular,
      margin: '0 0 var(--spacer-xl) 0',
    },

    strong: {
      fontWeight: theme.typography.fontWeightMedium,
    },

    ul: {
      margin: 'var(--spacer-l) 0 0 !important',

      [theme.breakpoints.up('sm')]: {
        margin: 'var(--spacer-m) 0 0 !important',
      },
    },

    li: {
      margin: 0,
      ...theme.typography.h5,
      fontWeight: theme.typography.fontWeightRegular,
      paddingLeft: 'calc(30px + var(--spacer-s)) !important', // width of svg checkmark plus spacer-s.
      display: 'flex',
      alignItems: 'center',
      marginTop: '0 !important',
      marginBottom: 'var(--spacer-xs) !important',
      minHeight: '28px',

      [theme.breakpoints.up('sm')]: {
        minHeight: '32px',
      },

      [theme.breakpoints.up('lg')]: {
        minHeight: '36px',
      },

      p: {
        margin: 0,
        ...theme.typography.h5,
        fontWeight: theme.typography.fontWeightRegular,
      },

      '&:last-child': {
        marginBottom: '0 !important',
      },

      '&::before': {
        content:
          'url("data:image/svg+xml;base64,' +
          btoa(
            '<svg viewBox="0 0 36 36" \n' +
              '  xmlns="http://www.w3.org/2000/svg">\n' +
              '  <circle fill="#0050A0" cx="18" cy="18" r="15"/>\n' +
              '    <path d="M15.7511 21.1264L12.6236 17.9989L11.5586 19.0564L15.7511 23.2489L24.7511 14.2489L23.6936 13.1914L15.7511 21.1264Z" fill="white"/>\n' +
              '</svg>',
          ) +
          '") !important',
        display: 'inline-block',
        width: '28px',
        height: '28px',
        marginRight: 'var(--spacer-s)',
        verticalAlign: 'middle',

        [theme.breakpoints.up('sm')]: {
          width: '32px',
          height: '32px',
        },

        [theme.breakpoints.up('lg')]: {
          width: '36px',
          height: '36px',
        },
      },
    },

    p: {
      margin: 0,
    },

    'a.mewa-editor-primary-button': {
      marginTop: 'var(--spacer-xl)',
      border: `2px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
      paddingTop: '0px',
      paddingBottom: '1px',
      width: '100%',

      [theme.breakpoints.up('sm')]: {
        width: 'auto',
        paddingTop: '1px',
        paddingBottom: '2px',
        paddingLeft: '14px',
      },

      [theme.breakpoints.up('md')]: {
        paddingLeft: '17px',
      },

      [theme.breakpoints.up('lg')]: {
        paddingTop: '2px',
        paddingBottom: '3px',
        paddingLeft: '20px',
      },

      '&::before': {
        content:
          'url("data:image/svg+xml;base64,' +
          btoa(
            '<svg viewBox="0 0 48 48" \n' +
              '  xmlns="http://www.w3.org/2000/svg">\n' +
              '  <path fill="#E60021" fill-rule="evenodd" d="M37.954 24 28.88 15.34l-1.38 1.446L34.01 23H10.046v2H34.01l-6.51 6.215 1.38 1.446 9.074-8.66Z" clip-rule="evenodd"/>\n' +
              '</svg>',
          ) +
          '")',
        display: 'inline-block',
        marginRight: 'var(--spacer-xs)',
        verticalAlign: 'middle',
        width: '36px',
        height: '36px',

        [theme.breakpoints.up('sm')]: {
          width: '42px',
          height: '42px',
        },

        [theme.breakpoints.up('md')]: {
          width: '44px',
          height: '44px',
        },

        [theme.breakpoints.up('lg')]: {
          width: '48px',
          height: '48px',
        },
      },

      '&[target="_blank"]': {
        '> span': {
          margin: '0 !important',
          width: '36px',
          height: '36px',

          [theme.breakpoints.up('sm')]: {
            width: '42px',
            height: '42px',
          },

          [theme.breakpoints.up('md')]: {
            width: '44px',
            height: '44px',
          },

          [theme.breakpoints.up('lg')]: {
            width: '48px',
            height: '48px',
          },
        },

        '&::before': {
          display: 'none',
        },
      },

      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.main,
        color: 'var(--color-white)',

        '&::before': {
          content:
            'url("data:image/svg+xml;base64,' +
            btoa(
              '<svg viewBox="0 0 48 48" \n' +
                '  xmlns="http://www.w3.org/2000/svg">\n' +
                '  <path fill="#FFFFFF" fill-rule="evenodd" d="M37.954 24 28.88 15.34l-1.38 1.446L34.01 23H10.046v2H34.01l-6.51 6.215 1.38 1.446 9.074-8.66Z" clip-rule="evenodd"/>\n' +
                '</svg>',
            ) +
            '")',
        },
      },
    },
  }),
);
